/**
 * What the GUEST can decode in hardware, probed once and cached.
 *
 * The host picks the send codec from its own encoder capability alone, so a host with a
 * hardware AV1 encoder happily pins a phone that decodes AV1 in software. Fixing that needs
 * the guest's decode capability on the wire, and the guest cannot afford to discover it
 * during connect: `mediaCapabilities.decodingInfo` is an async round trip into the platform
 * decoder, and connect is the one path where added latency is visible to the player.
 *
 * The probe runs once and connect waits for its bounded result. Undefined means readiness is
 * still pending; an empty result is terminal because no hardware-only session is possible.
 *
 * Relationship to `preferHardwareVideoCodecs`: that module answers "which of these codecs
 * should I rank first" and deliberately collapses its per-family verdicts into an ordering,
 * so it cannot report which families were hardware. This module asks the same
 * `mediaCapabilities.decodingInfo` question with the same `prefer-hardware` hint, and adds
 * the caching and the family-name projection — nothing else.
 */

import {
  DEFAULT_HW_PROBE_VIDEO,
  videoCodecFamily,
  type HardwareProbeVideoTarget,
} from './preferHardwareVideoCodecs';

/**
 * The one question this module asks, per codec family. Injectable so the cache can be
 * tested without a browser, and so a caller can supply a probe of its own.
 */
export type DecodeCapabilityProbe = (
  contentType: string,
) => Promise<{ supported?: boolean; powerEfficient?: boolean }>;

/** The only families active negotiation is allowed to select, in product order. */
const DEFAULT_DECODE_FAMILIES = ['h265', 'h264'] as const;

/**
 * Families whose hardware decode is SHIPPED but whose probe reports "no".
 *
 * Safari/WebKit is the known case. VideoToolbox decodes HEVC in hardware on every
 * Apple Silicon / iPhone / iPad that ships, and the guest can offer H.265 — but
 * `decodingInfo` on WebKit is incomplete for HEVC and answers unsupported /
 * software even when hardware decode exists. If that negative answer reached the
 * host's send-codec pin, the host would exclude H.265 — the ONLY hardware escape a
 * Safari guest has (its H.264 offer is constrained-baseline-only,
 * software-encodable on the host fleet) — and the whole hardware-hardware HEVC path
 * would be vetoed by a probe the platform cannot answer truthfully.
 *
 * A negative probe is therefore not trusted for these families; a positive one
 * still wins. The families stay listed so a future WebKit fix makes the platform
 * answer authoritative again.
 */
const WEBSHIM_HARDWARE_DECODE_FAMILIES = new Set(['h265']);

function isWebKit(): boolean {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent;
  // Safari and AppleWebKit identify themselves; Chrome/Chromium also contain
  // "AppleWebKit" but not "Safari" (Chrome's UA names "Chrome" instead), which is
  // exactly the distinction the app's own isSafari() makes.
  return userAgent.includes('Safari') && !userAgent.includes('Chrome') && !userAgent.includes('Chromium');
}

/** Representative RTP mime types, so each family is asked about in its own terms. */
const FAMILY_MIME_TYPE: Record<string, string> = {
  h264: 'video/H264',
  h265: 'video/H265',
};

/**
 * How long the whole probe may take before it is abandoned as unanswerable.
 *
 * A platform that never answers is indistinguishable from one that cannot, and holding an
 * in-flight promise forever would keep every later caller waiting on it. Well clear of a
 * real decodingInfo call, which is a local query.
 */
const PROBE_BUDGET_MS = 2_000;

let cachedReadiness: GuestDecodeReadiness | undefined;
let inFlightProbe: Promise<GuestDecodeReadiness> | null = null;
/** Bumped by reset() so a probe still in flight cannot write into the cleared cache. */
let cacheGeneration = 0;
/** Memoized projection for the `primeGuestDecodeCapability` compatibility wrapper. */
let compatProjection: Promise<string[]> | null = null;
let compatGeneration = -1;

function contentTypeFor(family: string): string {
  const mimeType = FAMILY_MIME_TYPE[family] ?? `video/${family}`;
  return mimeType.includes('/') ? mimeType : `video/${mimeType}`;
}

/** Normalizes caller-supplied names ('hevc', 'AVC') onto the family vocabulary, deduped. */
function normalizeFamilies(families: readonly string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const family of families) {
    const key = videoCodecFamily(family);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    normalized.push(key);
  }
  return normalized;
}

/** `mediaCapabilities.decodingInfo`, adapted to the injectable probe shape. */
function browserDecodeProbe(
  video: HardwareProbeVideoTarget = DEFAULT_HW_PROBE_VIDEO,
): DecodeCapabilityProbe | null {
  const mediaCapabilities = globalThis.navigator?.mediaCapabilities;
  const decodingInfo = mediaCapabilities?.decodingInfo;
  if (!decodingInfo) return null;

  return (contentType: string) =>
    decodingInfo.call(mediaCapabilities, {
      type: 'webrtc',
      video: {
        contentType,
        width: video.width,
        height: video.height,
        bitrate: video.bitrate,
        framerate: video.framerate,
      },
      // Non-standard Chromium hint, the same one preferHardwareVideoCodecs relies on: it is
      // what makes Chromium answer for the hardware path. Ignored elsewhere.
      hardwareAcceleration: 'prefer-hardware',
    } as MediaDecodingConfiguration);
}

type FamilyProbeOutcome = { families: string[]; anyAnswered: boolean };

/** Distinguishes a genuine answer from "the platform could not answer". */
type BoundedProbeResult = { families: string[]; answered: boolean };

async function probeFamilies(
  probe: DecodeCapabilityProbe,
  families: string[],
): Promise<FamilyProbeOutcome> {
  let anyAnswered = false;
  const verdicts = await Promise.all(
    families.map(async (family) => {
      try {
        const info = await probe(contentTypeFor(family));
        // The platform produced a value: this family has a real answer either way.
        anyAnswered = true;
        const probeSaysHardware = info?.supported === true && info?.powerEfficient === true;
        if (probeSaysHardware) return true;
        // Negative probe answers are untrusted on WebKit for families whose hardware
        // decode ships (VideoToolbox HEVC) but whose decodingInfo cannot report it.
        return isWebKit() && WEBSHIM_HARDWARE_DECODE_FAMILIES.has(family);
      } catch {
        // A throw is "could not answer", not "answered no" — it does not set anyAnswered.
        return isWebKit() && WEBSHIM_HARDWARE_DECODE_FAMILIES.has(family);
      }
    }),
  );
  return { families: families.filter((_, index) => verdicts[index]), anyAnswered };
}

/**
 * "The probe could not answer" is NOT the same as "the probe answered no hardware".
 *
 * An absent `decodingInfo`, every family throwing, or an expired budget all land here.
 * Treating any of them as a terminal [] pins the page to "this guest has no hardware
 * decoder" for its whole life and can make the guest unable to join at all. `answered`
 * distinguishes the two so only a genuine answer is cached.
 */
async function runBoundedProbe(
  probe: DecodeCapabilityProbe | null,
  families: string[],
): Promise<BoundedProbeResult> {
  // Absent probe: the platform cannot be asked. Unknown, not a negative answer.
  if (!probe) return { families: [], answered: false };
  if (families.length === 0) return { families: [], answered: true };

  let budget: ReturnType<typeof setTimeout> | undefined;
  const abandonUnanswered = new Promise<BoundedProbeResult>((resolve) => {
    budget = setTimeout(() => resolve({ families: [], answered: false }), PROBE_BUDGET_MS);
  });

  try {
    const raced = await Promise.race([
      probeFamilies(probe, families).then((outcome) => ({
        families: outcome.families,
        answered: outcome.anyAnswered,
      })),
      abandonUnanswered,
    ]);
    return raced;
  } catch {
    return { families: [], answered: false };
  } finally {
    if (budget) clearTimeout(budget);
  }
}

/** Three-state outcome of the guest hardware-decode probe. */
export type GuestDecodeReadinessKind = 'available' | 'terminal-empty' | 'unanswered';

export interface GuestDecodeReadiness {
  kind: GuestDecodeReadinessKind;
  /** Present only when kind === 'available'. */
  families?: string[];
}

/**
 * Start the probe. Idempotent and safe to call more than once; the first call wins and
 * later calls return the same promise. Never throws.
 *
 * Only an ANSWERED probe is cached. An unanswered probe (absent API, throw, or timeout) is
 * deliberately not cached, so a later connect re-probes instead of inheriting a timeout as
 * a permanent verdict.
 */
export function primeGuestDecodeReadiness(options?: {
  probe?: DecodeCapabilityProbe | null;
  families?: string[];
}): Promise<GuestDecodeReadiness> {
  if (cachedReadiness !== undefined) return Promise.resolve(cachedReadiness);
  if (inFlightProbe) return inFlightProbe;

  const probe = options && 'probe' in options ? (options.probe ?? null) : browserDecodeProbe();
  const families = normalizeFamilies(options?.families ?? DEFAULT_DECODE_FAMILIES);

  const generation = cacheGeneration;
  inFlightProbe = runBoundedProbe(probe, families).then((result) => {
    if (generation !== cacheGeneration) {
      return result.answered
        ? { kind: result.families.length ? 'available' : 'terminal-empty', ...(result.families.length ? { families: result.families } : {}) }
        : { kind: 'unanswered' };
    }
    // The probe has settled: clear the single-flight guard either way, so an unanswered
    // probe does not leave a stale resolved promise for the next prime to inherit.
    inFlightProbe = null;
    if (!result.answered) {
      // Do NOT cache the verdict. The next prime asks again.
      compatProjection = null;
      return { kind: 'unanswered' } as GuestDecodeReadiness;
    }
    const readiness: GuestDecodeReadiness = result.families.length
      ? { kind: 'available', families: result.families }
      : { kind: 'terminal-empty' };
    cachedReadiness = readiness;
    return readiness;
  });
  return inFlightProbe;
}

/**
 * Start the probe, projected onto the family list callers on the connect critical path
 * expect. Compatibility wrapper over `primeGuestDecodeReadiness`.
 *
 * Resolves to the hardware families when available and to [] otherwise. An unanswered probe
 * resolves to [] but is NOT cached; `getGuestDecodeCapability()` still returns undefined in
 * that case so callers can tell "unknown" from "answered nothing".
 */
export function primeGuestDecodeCapability(options?: {
  probe?: DecodeCapabilityProbe | null;
  families?: string[];
}): Promise<string[]> {
  // Memoize the projected promise so concurrent primes share one instance, matching the
  // readiness probe's own single-flight guarantee ("probes once however many times primed").
  // It is cleared when the readiness probe lands unanswered, so the next prime re-probes.
  if (compatProjection && compatGeneration === cacheGeneration) {
    return compatProjection;
  }
  const generation = cacheGeneration;
  const projection = primeGuestDecodeReadiness(options).then((readiness) => {
    if (generation === cacheGeneration && readiness.kind === 'unanswered') compatProjection = null;
    return readiness.families ?? [];
  });
  compatProjection = projection;
  compatGeneration = generation;
  return projection;
}

/**
 * Synchronous read for callers on the connect critical path. Returns undefined until the
 * probe has ANSWERED (or forever, if it never can). An empty array means the probe answered
 * that no hardware family is available. Session creation must wait for the bounded probe
 * before advertising.
 */
export function getGuestDecodeCapability(): string[] | undefined {
  if (cachedReadiness === undefined) return undefined;
  return cachedReadiness.families ?? [];
}

/** Synchronous three-state read. */
export function getGuestDecodeReadiness(): GuestDecodeReadiness | undefined {
  return cachedReadiness;
}

/** Test seam. Clears the cache and any in-flight probe. */
export function resetGuestDecodeCapability(): void {
  cacheGeneration += 1;
  cachedReadiness = undefined;
  inFlightProbe = null;
  compatProjection = null;
}
